import { ColumnDef } from '@tanstack/react-table';
import { GameRoundHistory } from '@/types';
import { DataTable } from '@/components/common/DataTable';
import { useEffect, useState } from 'react';
import { apiClient } from '@/utils/api';

export interface FetchRoundHistoryResponse {
  data: GameRoundHistory[];
  pagination: {
    totalItems: number;
    limit: number;
    currentPage: number;
    totalPage: number;
  };
}

export default function GameRoundHistoryComp() {
  const [data, setData] = useState<GameRoundHistory[]>([]);
  const [loading, setLoading] = useState(true);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchRoundHistory = async (page = 0, size = 10) => {
    try {
      setLoading(true);

      const res = await apiClient.get<FetchRoundHistoryResponse>('/reports/round-history', {
        params: {
          skip: page * size,
          take: size,
        },
      });

      setData(res.data.data);
      setTotal(res.data.pagination.totalItems);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoundHistory(pageIndex, pageSize);
  }, [pageIndex, pageSize]);

  const roundHistoryColumns: ColumnDef<GameRoundHistory>[] = [
    {
      accessorKey: 'roundId',
      header: 'Round ID',
    },
    {
      accessorKey: 'dragonCard',
      header: 'Dragon Card',
    },
    {
      accessorKey: 'tigerCard',
      header: 'Tiger Card',
    },
    {
      accessorKey: 'result',
      header: 'Result',
      cell: ({ row }) => {
        const result = row.original.result;

        const color =
          result === 'Dragon'
            ? 'text-red-500'
            : result === 'Tiger'
              ? 'text-blue-500'
              : 'text-yellow-400';

        return <span className={`font-semibold ${color}`}>{result}</span>;
      },
    },
    {
      accessorKey: 'totalBetAmount',
      header: 'Total Bet',
      cell: ({ row }) => <span>₹{row.original.totalBetAmount.toLocaleString()}</span>,
    },
    {
      accessorKey: 'totalPayout',
      header: 'Total Payout',
      cell: ({ row }) => <span>₹{row.original.totalPayout.toLocaleString()}</span>,
    },
    {
      accessorKey: 'profitLoss',
      header: 'Profit/Loss',
      cell: ({ row }) => {
        const { profit, loss } = row.original;
        const value = profit > 0 ? profit : -loss;

        return (
          <span
            className={profit > 0 ? 'text-green-500 font-semibold' : 'text-red-500 font-semibold'}
          >
            ₹{Math.abs(value).toLocaleString()}
          </span>
        );
      },
    },
    {
      accessorKey: 'timestamp',
      header: 'Timestamp',
      cell: ({ row }) =>
        new Date(row.original.timestamp).toLocaleString('en-IN', {
          dateStyle: 'medium',
          timeStyle: 'short',
        }),
    },
  ];

  const handleExport = (rows: GameRoundHistory[]) => {
    if (!rows.length) return;

    const header = [
      'Round ID',
      'Dragon Card',
      'Tiger Card',
      'Result',
      'Total Bet Amount',
      'Total Payout',
      'Timestamp',
    ];

    const csvRows = rows.map((row) =>
      [
        row.roundId,
        row.dragonCard,
        row.tigerCard,
        row.result,
        row.totalBetAmount,
        row.totalPayout,
        row.timestamp,
      ].join(',')
    );
    const csvString = [header.join(','), ...csvRows].join('\n');

    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'game_round_history.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-10">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Round-wise Game History</h2>
          <p className="text-muted-foreground">
            History of all completed game rounds, showing Dragon/Tiger cards, game outcomes, total
            bets, and payouts. Useful for audits and dispute resolution.
          </p>
        </div>
      </div>
      <DataTable
        columns={roundHistoryColumns}
        data={data}
        enableExport
        enableSelection
        pageIndex={pageIndex}
        pageSize={pageSize}
        total={total}
        onPageChange={(page) => setPageIndex(page)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />
    </div>
  );
}
